import { BannerEntry, BannerEvent, BannerOffers } from '../../api/bannerOffers/types';

export const MAX_LABEL_LENGTH = 22;
export const MAX_CTA_LENGTH = 10;

/** Date -> 'YYYY-MM-DD' using LOCAL date parts (no timezone shift). */
export const dateToLocalString = (date: Date): string => {
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
};

/** 'YYYY-MM-DD' -> Date at local midnight. */
export const localStringToDate = (value?: string): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const dayNumber = (value: string): number => {
  const [y, m, d] = value.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};

export const sortEventsByStart = <T extends BannerEvent>(events: T[]): T[] =>
  [...events].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

export const isEventInPast = (event: BannerEvent, today = dateToLocalString(new Date())): boolean => event.end < today;

export const eventsOverlap = (a: BannerEvent, b: BannerEvent): boolean => a.start <= b.end && b.start <= a.end;

export const getOverlappingIndexes = (events: BannerEvent[]): Set<number> => {
  const result = new Set<number>();
  events.forEach((a, i) => {
    events.forEach((b, j) => {
      if (i < j && eventsOverlap(a, b)) {
        result.add(i);
        result.add(j);
      }
    });
  });
  return result;
};

export type PickedBanner = { source: string; entry: BannerEntry };

/** Mirrors the app's picking rule for a local date 'YYYY-MM-DD'. */
export const pickBannerForDate = (config: BannerOffers, date: string): PickedBanner => {
  let best: BannerEvent | undefined;
  config.events.forEach((event) => {
    if (event.start <= date && date <= event.end) {
      const length = dayNumber(event.end) - dayNumber(event.start);
      if (!best || length < dayNumber(best.end) - dayNumber(best.start)) {
        best = event; // strict < keeps list order on ties
      }
    }
  });
  if (best) {
    return { source: `Event: ${best.occasion || best.label}`, entry: best };
  }

  const weekday = new Date(dayNumber(date) * 86400000).getUTCDay();
  if (weekday === 0) {
    return { source: 'Sunday entry', entry: config.weekly.sunday };
  }
  if (weekday === 1) {
    return { source: 'Monday entry', entry: config.weekly.monday };
  }
  return { source: 'Default entry', entry: config.default };
};

/** Inclusive number of days an event runs. */
export const eventDurationDays = (event: Pick<BannerEvent, 'start' | 'end'>): number =>
  dayNumber(event.end) - dayNumber(event.start) + 1;

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const daysUntil = (to: string, from = dateToLocalString(new Date())): number => dayNumber(to) - dayNumber(from);

export type EventStatus = 'live' | 'upcoming' | 'past';

export const getEventStatus = (event: BannerEvent, today = dateToLocalString(new Date())): EventStatus => {
  if (event.end < today) {
    return 'past';
  }
  return event.start <= today ? 'live' : 'upcoming';
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatShort = (value: string, withYear: boolean): string => {
  const [y, m, d] = value.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}${withYear ? `, ${y}` : ''}`;
};

/** 'Oct 9 – Oct 27, 2026' or 'Nov 1, 2026' for single-day events. */
export const formatDateRange = (start: string, end: string): string => {
  if (!start) {
    return '';
  }
  if (!end || start === end) {
    return formatShort(start, true);
  }
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  return `${formatShort(start, !sameYear)} – ${formatShort(end, true)}`;
};

export const addDaysToLocalString = (value: string, days: number): string => {
  const date = localStringToDate(value);
  if (!date) {
    return '';
  }
  date.setDate(date.getDate() + days);
  return dateToLocalString(date);
};
